import gql from 'graphql-tag';

import { executeGqlMutation } from 'src/graphql/client';
import { FRAGMENT_JOB_ID, waitForJobWithEntityResult } from 'src/graphql/job';
import { stringifyIfPresent } from 'src/lib/common';
import { getGqlFragmentName, getSingleProp } from 'src/lib/graphql';

import { FRAGMENT_RECOMPOSE_REQUEST } from './schema';

/**
 * "recompose" generation for short-form
 * @param {Object} params
 * @param {Object} [options]
 * @returns {Promise<Object[]>}
 */
export const rephraseTextsForDocumentEditor = async (
  {
    projectId,
    originalVariationId,
    assetType,
    generationTool,
    brief,
    scoringMethod,
    productName,
    text,
    recomposeMode,
    keywords,
    language,
    numberOfVariations,
    debugAcPrompt,
    languageTargetFormality,
    sourceType,
    onlyImproveScore,
    customerPersonaId,
    annotations,
    manualInstructions,
    tovInput,
    forceZeroScore,
    rephraseContext,
    structureInstructionsData,
    imageResourceId,
    imageStyleId,
    imageOverlayProperties,
  },
  options = null
) => {
  const resultFragment = FRAGMENT_RECOMPOSE_REQUEST;
  const fragment = options?.async ? FRAGMENT_JOB_ID : resultFragment;
  const mutation = gql`
    mutation RephraseTextsForDocumentEditor(
      $async: Boolean
      $projectId: ID!
      $originalVariationId: ID
      $assetType: String
      $generationTool: GenerationToolEnum!
      $brief: BriefInput
      $scoringMethod: ScoringMethodInput
      $productName: String
      $text: String!
      $recomposeMode: String
      $keywords: String
      $language: String!
      $languageTargetFormality: LanguageTargetFormalityEnum
      $numberOfVariations: Int!
      $debugAcPrompt: String
      $sourceType: String
      $onlyImproveScore: Boolean
      $customerPersonaId: ID
      $annotations: [SuggestionAnnotationInput!]
      $manualInstructions: String
      $tovInput: TovInput
      $forceZeroScore: Boolean
      $rephraseContext: JSON
      $structureInstructionsData: JSON
      $imageResourceId: ID
      $imageStyleId: ID
      $imageOverlayProperties: ImageOverlayTextInput
    ) {
      rephraseTextsForDocumentEditor(
        async: $async
        projectId: $projectId
        originalVariationId: $originalVariationId
        assetType: $assetType
        generationTool: $generationTool
        brief: $brief
        scoringMethod: $scoringMethod
        productName: $productName
        text: $text
        recomposeMode: $recomposeMode
        keywords: $keywords
        language: $language
        languageTargetFormality: $languageTargetFormality
        numberOfVariations: $numberOfVariations
        debugAcPrompt: $debugAcPrompt
        sourceType: $sourceType
        onlyImproveScore: $onlyImproveScore
        customerPersonaId: $customerPersonaId
        annotations: $annotations
        manualInstructions: $manualInstructions
        tovInput: $tovInput
        forceZeroScore: $forceZeroScore
        rephraseContext: $rephraseContext
        structureInstructionsData: $structureInstructionsData
        imageResourceId: $imageResourceId
        imageStyleId: $imageStyleId
        imageOverlayProperties: $imageOverlayProperties
      ) {
        ...${getGqlFragmentName(fragment)}
      }
    }
    ${fragment}
  `;

  const variables = {
    async: options?.async ?? false,
    projectId,
    originalVariationId,
    assetType,
    generationTool,
    brief,
    scoringMethod,
    productName,
    text,
    recomposeMode,
    keywords,
    language,
    numberOfVariations,
    debugAcPrompt,
    languageTargetFormality,
    sourceType,
    onlyImproveScore,
    customerPersonaId,
    annotations,
    manualInstructions,
    tovInput,
    forceZeroScore,
    rephraseContext,
    structureInstructionsData: stringifyIfPresent(structureInstructionsData),
    imageResourceId,
    imageStyleId,
    imageOverlayProperties,
  };

  const response = await executeGqlMutation({
    mutation,
    variables,
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  const result = getSingleProp(response.data);
  if (!options?.async) {
    // support old BE, which returns a single object instead of an array
    return Array.isArray(result) ? result : [result];
  }

  const job = Array.isArray(result) ? result[0] : result;
  return waitForJobWithEntityResult(job.id, resultFragment, {
    abortSignal: options?.abortSignal,
    list: true,
  });
};
